"use client";

import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type ActivitySessionCommentItem,
  type ActivitySessionCommentLikeListResponse,
  type ActivitySessionCommentListResponse,
  createActivitySessionComment,
  deleteActivitySessionComment,
  listActivitySessionCommentLikes,
  listActivitySessionComments,
  setActivitySessionCommentPinned,
  toggleActivitySessionCommentLike,
} from "@/actions/activity-session-interaction";
import {
  type CommentThreadPosition,
  createOptimisticCommentId,
  deriveOptimisticCommentThreadPosition,
  getCommentSubtreeIds,
} from "@/lib/comments/threading";
import {
  type MusicAttachment,
  musicAttachmentInput,
} from "@/lib/music/attachment";
import type { CommentMedia, CommentVoiceNote } from "@/validations/comment";

type CommentViewerInfo = {
  id?: string;
  displayName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
};

type CreateActivitySessionCommentPayload = {
  activitySessionId: number;
  content: string;
  parentCommentId?: number | null;
  voice?: CommentVoiceNote | null;
  media?: CommentMedia | null;
  viewer?: CommentViewerInfo;
  /** A song reply: the full snapshot paints the optimistic card; only its id goes up. */
  song?: MusicAttachment | null;
};

type CommentMeta = {
  tempId?: string;
  isPending?: boolean;
};

type CommentsPage = Omit<ActivitySessionCommentListResponse, "comments"> & {
  comments: Array<ActivitySessionCommentItem & CommentMeta>;
};

type CommentsInfiniteData = InfiniteData<CommentsPage, number | null>;

type ToggleCommentLikeVariables = {
  commentId: number;
  activitySessionId: number;
  currentlyLiked: boolean;
};

export const activitySessionCommentsKeys = {
  comments: (activitySessionId: number) =>
    ["activitySessions", "comments", activitySessionId] as const,
  commentLikes: (commentId: number) =>
    ["activitySessions", "comment-likes", commentId] as const,
};

export function useActivitySessionComments(
  activitySessionId: number | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { enabled = true, limit = 100 } = options ?? {};

  return useInfiniteQuery({
    queryKey: activitySessionCommentsKeys.comments(activitySessionId ?? 0),
    enabled: Boolean(activitySessionId && enabled),
    queryFn: async ({ pageParam }) => {
      if (!activitySessionId) {
        return {
          comments: [],
          hasMore: false,
          nextCursor: null,
        } satisfies CommentsPage;
      }

      const result = await listActivitySessionComments({
        activitySessionId,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch comments");
      }

      return {
        ...result.data,
        comments: result.data.comments.map((comment) => ({
          ...comment,
          isPending: false,
        })),
      } satisfies CommentsPage;
    },
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 15_000,
    // Other people change this list, and notifications link straight into
    // it; with `false` the 24h cache served it stale until an app restart.
    refetchOnMount: true,
    placeholderData: keepPreviousData,
  });
}

export function useCreateActivitySessionCommentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["activitySessions", "comments", "create"],
    mutationFn: async (payload: CreateActivitySessionCommentPayload) => {
      const result = await createActivitySessionComment({
        activitySessionId: payload.activitySessionId,
        content: payload.content,
        parentCommentId: payload.parentCommentId ?? null,
        voice: payload.voice ?? null,
        media: payload.media ?? null,
        music: payload.song ? musicAttachmentInput(payload.song) : null,
      });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to create comment");
      }
      return { id: result.data.id, payload };
    },
    onMutate: async (payload) => {
      const key = activitySessionCommentsKeys.comments(
        payload.activitySessionId,
      );
      await queryClient.cancelQueries({ queryKey: key });

      const prev = queryClient.getQueryData<CommentsInfiniteData>(key);
      const threadPosition = deriveOptimisticCommentThreadPosition(
        payload.parentCommentId,
        prev?.pages,
      );
      const optimistic = threadPosition
        ? buildOptimisticActivitySessionComment(payload, threadPosition)
        : null;

      if (optimistic) {
        queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
          prependComment(old, optimistic),
        );
      }

      return {
        prev,
        optimisticId: optimistic?.tempId,
        activitySessionId: payload.activitySessionId,
      };
    },
    onError: (_err, payload, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          activitySessionCommentsKeys.comments(payload.activitySessionId),
          ctx.prev,
        );
      }
    },
    onSuccess: (result, payload, ctx) => {
      const key = activitySessionCommentsKeys.comments(
        payload.activitySessionId,
      );
      queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
        replaceCommentId(old, ctx?.optimisticId, result.id),
      );
    },
    onSettled: (_data, _error, payload) => {
      queryClient.invalidateQueries({
        queryKey: activitySessionCommentsKeys.comments(
          payload.activitySessionId,
        ),
      });
    },
  });
}

export function useDeleteActivitySessionCommentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["activitySessions", "comments", "delete"],
    mutationFn: async ({ id }: { id: number; activitySessionId: number }) => {
      const result = await deleteActivitySessionComment({ id });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to delete comment");
      }
      return { id };
    },
    onMutate: async ({
      id,
      activitySessionId,
    }: {
      id: number;
      activitySessionId: number;
    }) => {
      const key = activitySessionCommentsKeys.comments(activitySessionId);
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<CommentsInfiniteData>(key);

      queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
        removeComment(old, id),
      );

      return { prev, activitySessionId };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          activitySessionCommentsKeys.comments(vars.activitySessionId),
          ctx.prev,
        );
      }
    },
    onSettled: (_data, _error, vars) => {
      queryClient.invalidateQueries({
        queryKey: activitySessionCommentsKeys.comments(vars.activitySessionId),
      });
    },
  });
}

export function useToggleActivitySessionCommentLikeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["activitySessions", "comments", "toggle-like"],
    mutationFn: async ({ commentId }: ToggleCommentLikeVariables) => {
      const result = await toggleActivitySessionCommentLike({ commentId });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to toggle like");
      }
      return { liked: result.data.liked };
    },
    onMutate: async ({
      commentId,
      activitySessionId,
      currentlyLiked,
    }: ToggleCommentLikeVariables) => {
      const key = activitySessionCommentsKeys.comments(activitySessionId);
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<CommentsInfiniteData>(key);

      queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
        updateComment(old, commentId, (comment) => ({
          ...comment,
          isLikedByViewer: !currentlyLiked,
          likeCount: Math.max(0, comment.likeCount + (currentlyLiked ? -1 : 1)),
        })),
      );

      return { prev, activitySessionId };
    },
    onError: (_err, vars, ctx) => {
      if (!ctx?.prev) return;
      queryClient.setQueryData(
        activitySessionCommentsKeys.comments(vars.activitySessionId),
        ctx.prev,
      );
    },
    onSettled: (_data, _error, vars) => {
      queryClient.invalidateQueries({
        queryKey: activitySessionCommentsKeys.comments(vars.activitySessionId),
      });
    },
  });
}

export function useSetActivitySessionCommentPinnedMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["activitySessions", "comments", "pin"],
    mutationFn: async (input: {
      activitySessionId: number;
      commentId: number;
      isPinned: boolean;
    }) => {
      const result = await setActivitySessionCommentPinned(input);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    },
    onSettled: (_data, _error, input) => {
      queryClient.invalidateQueries({
        queryKey: activitySessionCommentsKeys.comments(input.activitySessionId),
      });
    },
  });
}

type ActivitySessionCommentLikesPage = ActivitySessionCommentLikeListResponse;

export function useActivitySessionCommentLikes(
  commentId: number | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { enabled = true, limit = 30 } = options ?? {};

  return useInfiniteQuery({
    queryKey: activitySessionCommentsKeys.commentLikes(commentId ?? 0),
    enabled: Boolean(commentId && enabled),
    queryFn: async ({ pageParam }) => {
      if (!commentId) {
        return {
          likes: [],
          hasMore: false,
          nextCursor: null,
        } satisfies ActivitySessionCommentLikesPage;
      }

      const result = await listActivitySessionCommentLikes({
        commentId,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch comment likes");
      }

      return result.data;
    },
    initialPageParam: null as ActivitySessionCommentLikesPage["nextCursor"],
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 15_000,
    // Other people change this list, and notifications link straight into
    // it; with `false` the 24h cache served it stale until an app restart.
    refetchOnMount: true,
    placeholderData: keepPreviousData,
  });
}

// Helper functions
function buildOptimisticActivitySessionComment(
  payload: CreateActivitySessionCommentPayload,
  threadPosition: CommentThreadPosition,
): ActivitySessionCommentItem & CommentMeta {
  const id = createOptimisticCommentId();
  const tempId = `temp-comment-${Math.abs(id)}`;
  const authorName =
    payload.viewer?.displayName || payload.viewer?.username || "You";

  return {
    id,
    tempId,
    isPending: true,
    activitySessionId: payload.activitySessionId,
    parentCommentId: threadPosition.parentCommentId,
    rootCommentId: threadPosition.rootCommentId,
    depth: threadPosition.depth,
    content: payload.content,
    voice: payload.voice ?? null,
    media: payload.media ?? null,
    music: payload.song ?? null,
    isPinned: false,
    pinnedAt: null,
    createdAt: new Date(),
    author: {
      id: payload.viewer?.id ?? "temp",
      displayName: authorName,
      username: payload.viewer?.username ?? null,
      avatarUrl: payload.viewer?.avatarUrl ?? null,
    },
    likeCount: 0,
    isLikedByViewer: false,
  };
}

function prependComment(
  data: CommentsInfiniteData | undefined,
  comment: ActivitySessionCommentItem & CommentMeta,
): CommentsInfiniteData | undefined {
  if (!data) return data;
  const [first, ...rest] = data.pages;
  if (!first) return data;

  const deduped = first.comments.filter(
    (existing) =>
      (existing.tempId ?? existing.id) !== (comment.tempId ?? comment.id),
  );
  const nextFirst: CommentsPage = {
    ...first,
    comments: [comment, ...deduped],
  };

  return {
    ...data,
    pages: [nextFirst, ...rest],
  };
}

function replaceCommentId(
  data: CommentsInfiniteData | undefined,
  tempId: string | undefined,
  newId: number,
): CommentsInfiniteData | undefined {
  if (!data || !tempId) return data;

  const pages = data.pages.map((page) => {
    const comments = page.comments.map((comment) => {
      if (comment.tempId !== tempId) return comment;
      return {
        ...comment,
        id: newId,
        tempId: undefined,
        isPending: false,
      };
    });
    return { ...page, comments };
  });

  return { ...data, pages };
}

function removeComment(
  data: CommentsInfiniteData | undefined,
  id: number,
): CommentsInfiniteData | undefined {
  if (!data) return data;

  const removedIds = getCommentSubtreeIds(
    data.pages.flatMap((page) => page.comments),
    id,
  );

  const pages = data.pages.map((page) => ({
    ...page,
    comments: page.comments.filter((comment) => !removedIds.has(comment.id)),
  }));

  return { ...data, pages };
}

function updateComment(
  data: CommentsInfiniteData | undefined,
  commentId: number,
  updater: (
    comment: ActivitySessionCommentItem & CommentMeta,
  ) => ActivitySessionCommentItem & CommentMeta,
): CommentsInfiniteData | undefined {
  if (!data) return data;

  const pages = data.pages.map((page) => ({
    ...page,
    comments: page.comments.map((comment) =>
      comment.id === commentId ? updater(comment) : comment,
    ),
  }));

  return { ...data, pages };
}
